import { documentKey, ledgerBalance, rebuildOpening, } from './ledgers.js';
import { fromCents, toCents } from './money.js';
export const SQUARING_STATES = ['quadra', 'differenza', 'da-abbinare'];
export const SQUARING_STATE_LABELS = {
    quadra: 'Quadrano',
    differenza: 'Con differenza',
    'da-abbinare': 'Da abbinare all’Anagrafica',
};
/** A difference within this is rounding of the ledger, not a difference. */
export const SQUARING_TOLERANCE = '1.00';
/**
 * The rows of the year: every sub-ledger of the year, and every subject with
 * something open in the Tesoreria that is not in the ledger. Pro forma are
 * left out; a received self-billing is the foreign invoice of the ledger, for its
 * taxable (3.17), the issued copy of it is nobody's credit.
 */
export function ledgerSquaring(input) {
    const end = `${input.year}-12-31`;
    const tolerance = toCents(SQUARING_TOLERANCE);
    const openOf = new Map();
    for (const invoice of input.invoices) {
        if (invoice.kind === 'proforma' || invoice.date > end)
            continue;
        if (invoice.selfBilling && invoice.direction === 'emessa')
            continue;
        const paid = invoice.settlements
            .filter((settlement) => settlement.date <= end)
            .reduce((sum, settlement) => sum + toCents(settlement.amount), 0);
        const open = toCents(invoice.netDue) - paid;
        if (open === 0)
            continue;
        const key = `${invoice.direction}|${invoice.subjectId ?? `nome:${invoice.counterpartyName}`}`;
        openOf.set(key, [
            ...(openOf.get(key) ?? []),
            {
                invoiceId: invoice.id,
                kind: invoice.kind,
                number: invoice.number,
                date: invoice.date,
                open: fromCents(open),
                ledgerOpen: null,
            },
        ]);
    }
    const used = new Set();
    const rows = input.accounts.map((account) => {
        const direction = account.cycle === 'attivo' ? 'emessa' : 'ricevuta';
        const key = account.subject ? `${direction}|${account.subject.id}` : null;
        // Two sub-ledgers of the same subject (two accounts): the Tesoreria goes with the first.
        const treasuryItems = key && !used.has(key) ? (openOf.get(key) ?? []) : [];
        if (key)
            used.add(key);
        const ledger = toCents(ledgerBalance(account.cycle, account.closing));
        const treasury = treasuryItems.reduce((sum, item) => sum + toCents(item.open), 0);
        const difference = treasury - ledger;
        const state = !account.subject
            ? 'da-abbinare'
            : Math.abs(difference) <= tolerance
                ? 'quadra'
                : 'differenza';
        // What the ledger still has open at 31 December, rebuilt from the lines of the year.
        const ledgerItems = state === 'differenza'
            ? rebuildOpening({
                cycle: account.cycle,
                opening: account.closing,
                previous: { opening: account.opening, entries: account.entries },
                following: [],
            }).items
            : [];
        const ledgerOpen = new Map();
        for (const item of ledgerItems) {
            if (item.kind !== 'documento' || !item.documentNumber)
                continue;
            const number = documentKey(item.documentNumber);
            ledgerOpen.set(number, (ledgerOpen.get(number) ?? 0) + toCents(item.open));
        }
        const treasuryOpen = new Set(treasuryItems.map((item) => documentKey(item.number)));
        return {
            accountId: account.id,
            cycle: account.cycle,
            name: account.name,
            subject: account.subject,
            ledger: fromCents(ledger),
            treasury: fromCents(treasury),
            difference: fromCents(difference),
            state,
            onlyTreasury: state === 'differenza'
                ? treasuryItems
                    .map((item) => {
                    const other = ledgerOpen.get(documentKey(item.number));
                    return { ...item, ledgerOpen: other === undefined ? null : fromCents(other) };
                })
                    .filter((item) => item.ledgerOpen === null || item.ledgerOpen !== item.open)
                : [],
            onlyLedger: ledgerItems
                .filter((item) => item.kind === 'ante' ||
                !item.documentNumber ||
                !treasuryOpen.has(documentKey(item.documentNumber)))
                .map((item) => ({
                documentNumber: item.documentNumber,
                documentDate: item.documentDate,
                description: item.kind === 'ante' ? 'Saldo senza dettaglio dell’anno prima' : item.description,
                open: item.open,
            })),
        };
    });
    // Open in the Tesoreria for a subject that the ledger of the year does not have.
    for (const [key, items] of openOf) {
        if (used.has(key))
            continue;
        const [direction, subjectId] = key.split('|');
        const treasury = items.reduce((sum, item) => sum + toCents(item.open), 0);
        if (Math.abs(treasury) <= tolerance)
            continue;
        const name = input.invoices.find((invoice) => invoice.id === items[0].invoiceId)?.counterpartyName ?? '';
        rows.push({
            accountId: null,
            cycle: direction === 'emessa' ? 'attivo' : 'passivo',
            name,
            subject: subjectId.startsWith('nome:') ? null : { id: subjectId, name },
            ledger: '0.00',
            treasury: fromCents(treasury),
            difference: fromCents(treasury),
            state: 'differenza',
            onlyTreasury: items,
            onlyLedger: [],
        });
    }
    return rows.sort((a, b) => a.cycle.localeCompare(b.cycle) || a.name.localeCompare(b.name));
}
//# sourceMappingURL=ledger-squaring.js.map